import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { GL_VP, US_STATES } from '../../resources/constants';
import { useChartAccountApi } from '../../resources/hooks/api/chartAccountApiHook';
import { getBranch } from '../../store/user/selectors';

const STATE_OPTIONS = US_STATES.map((state) => ({
  value: state.code,
  label: `${state.code} — ${state.name}`,
}));

/**
 * Create / edit form for a 1099 vendor.
 *
 * The field set mirrors what gl-api accepts on POST and PUT /tax_1099/vendors.
 * Two constraints are enforced here so the user finds out before a round trip:
 *
 *   - vendor_code is required on create and read-only on edit, because it is
 *     half of the branch uniqueness key and the API rejects changes to it.
 *   - a consent date cannot be entered while the consent box is unticked; the
 *     API rejects that combination as contradictory evidence.
 */

export interface VendorFormValues {
  id?: number;
  vendor_code: string;
  display_name: string;
  contact_name: string;
  email: string;
  phone: string;
  vendor_type: string;
  is_1099_eligible: string;
  default_form_type: string;
  default_box: string;
  // Number or null, never '': quick-create callers spread these values straight
  // into the API payload. The label only feeds the dropdown.
  default_account_chart_id: number | null;
  default_account_label: string;
  status: string;
  recipient_edelivery_consent: boolean;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  source?: string;
  qbo_id?: string;
}

export const EMPTY_VENDOR: VendorFormValues = {
  vendor_code: '',
  display_name: '',
  contact_name: '',
  email: '',
  phone: '',
  vendor_type: '',
  is_1099_eligible: '',
  default_form_type: '',
  default_box: '',
  default_account_chart_id: null,
  default_account_label: '',
  status: '1',
  recipient_edelivery_consent: false,
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  source: 'manual',
  qbo_id: '',
};

// Values are not agreed yet, so the labels live here rather than in a shared
// enum. Moving them to the API later means changing this list only.
const VENDOR_TYPES = [
  { value: '1', key: 'form.vendor_type.contractor' },
  { value: '2', key: 'form.vendor_type.sub_contractor' },
  { value: '3', key: 'form.vendor_type.both' },
];

// tax_1099_vendors.vendor_type: the sub-contractor option in VENDOR_TYPES below.
const SUB_CONTRACTOR_VENDOR_TYPE = '2';

// Contractor and both must name the expense account their payments default to
// on expense classification; gl-api rejects them without one.
const CONTRACTOR_VENDOR_TYPES = ['1', '3'];

/** The label an account carries in every GL dropdown: "Name(1234)". */
export const accountLabel = (account: { name?: string; account?: string } | null | undefined) =>
  account ? `${account.name}(${account.account})` : '';

const BOXES_BY_FORM_TYPE: Record<string, string[]> = {
  '1099-NEC': ['NEC-1'],
  '1099-MISC': ['MISC-1', 'MISC-2', 'MISC-3', 'MISC-6', 'MISC-10'],
};

const FORM_TYPES = Object.keys(BOXES_BY_FORM_TYPE);
const ALL_BOX_CODES = ['NEC-1', 'MISC-1', 'MISC-2', 'MISC-3', 'MISC-6', 'MISC-10'];

interface Props {
  show: boolean;
  saving: boolean;
  codeLoading?: boolean;
  initial: VendorFormValues;
  onClose: () => void;
  onSubmit: (values: VendorFormValues) => void;
}

const VendorFormModal: React.FC<Props> = ({ show, saving, codeLoading, initial, onClose, onSubmit }) => {
  const { t } = useTranslation('Tax1099Vendors');
  const [values, setValues] = useState<VendorFormValues>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const branchData: any = useSelector(getBranch);
  const { glAccountsList } = useChartAccountApi();

  const isEdit = Boolean(initial.id);
  const isContractor = CONTRACTOR_VENDOR_TYPES.includes(values.vendor_type);
  const isQuickBooks = values.source === 'qbo' || values.source === 'qbd';

  // Same list the expense classification debit dropdown offers -- everything
  // but bank accounts -- so the default can always be shown there.
  const searchAccounts = (searchText: string) =>
    new Promise<Array<{ value: number; label: string }>>((resolve) => {
      const params: Record<string, any> = {
        page: 1,
        per_page: 10,
        branch_id: Number(branchData?.id),
        parent_account_not_in: GL_VP.BANK_ACCOUNT.account.join(','),
      };
      if (searchText) params.name = searchText;
      glAccountsList(params, (_m: string, resp: any) => {
        resolve((resp?.data?.data?.result || []).map((e: any) => ({ value: e.id, label: accountLabel(e) })));
      }, () => resolve([]));
    });

  const availableBoxes = values.default_form_type
    ? BOXES_BY_FORM_TYPE[values.default_form_type] || []
    : ALL_BOX_CODES;

  const handleVendorTypeChange = (newVendorType: string) => {
    // A sub-contractor is reported outside this module, so it carries no form
    // or box and is force-excluded rather than derived from the W-9. The same
    // three values are already applied when an existing sub-contractor is
    // loaded for edit, and forced again on submit -- picking the type by hand
    // was the one path that left them alone, so the form showed defaults the
    // save would then overwrite.
    //
    // A contractor takes the opposite default: eligibility derived from the
    // W-9, because the entity type on the W-9 is what actually decides whether
    // a contractor is reportable. Its form and box are left alone -- those are
    // a real choice (NEC-1, MISC-1, ...) that this rule says nothing about.
    //
    // Set, not locked: these are defaults a person can still override before
    // saving, which is what the fields being editable elsewhere implies.
    // Switching type re-applies the new type's default, so a deliberate
    // override does not survive a change of type -- the same way the
    // sub-contractor values are re-applied.
    const isSub = newVendorType === SUB_CONTRACTOR_VENDOR_TYPE;
    setValues((previous) => ({
      ...previous,
      vendor_type: newVendorType,
      ...(isSub
        ? { is_1099_eligible: '0', default_form_type: '', default_box: '' }
        : { is_1099_eligible: '' }),
      // Only a contractor type keeps a default account; the API drops it otherwise.
      ...(CONTRACTOR_VENDOR_TYPES.includes(newVendorType)
        ? {}
        : { default_account_chart_id: null, default_account_label: '' }),
    }));
    setErrors((previous) => {
      const next = { ...previous };
      delete next.vendor_type;
      delete next.default_account_chart_id;
      delete next.is_1099_eligible;
      delete next.default_form_type;
      delete next.default_box;
      return next;
    });
  };

  const handleFormTypeChange = (newFormType: string) => {
    const boxes = BOXES_BY_FORM_TYPE[newFormType] || [];
    let nextBox = values.default_box;

    if (boxes.length === 1) {
      nextBox = boxes[0];
    } else if (!boxes.includes(nextBox)) {
      nextBox = '';
    }

    setValues((previous) => ({
      ...previous,
      default_form_type: newFormType,
      default_box: nextBox,
    }));
    setErrors((previous) => {
      const next = { ...previous };
      delete next.default_form_type;
      delete next.default_box;
      return next;
    });
  };

  useEffect(() => {
    const isSub = String(initial.vendor_type) === SUB_CONTRACTOR_VENDOR_TYPE;
    const initialForm = isSub ? '' : (initial.default_form_type || '');
    const boxes = BOXES_BY_FORM_TYPE[initialForm] || [];
    let initialBox = isSub ? '' : (initial.default_box || '');
    if (boxes.length === 1 && (!initialBox || !boxes.includes(initialBox))) {
      initialBox = boxes[0];
    }
    const autoCode = initial.vendor_code || '';
    setValues({
      ...initial,
      vendor_code: autoCode,
      source: initial.source || 'manual',
      is_1099_eligible: isSub ? '0' : (initial.is_1099_eligible ?? ''),
      default_form_type: initialForm,
      default_box: initialBox,
    });
    setErrors({});
  }, [initial, show]);

  const setField = (field: keyof VendorFormValues, value: any) => {
    setValues((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => {
      const next = { ...previous };
      delete next[field as string];
      return next;
    });
  };

  const validate = (): boolean => {
    const found: Record<string, string> = {};

    if (!isEdit && !values.vendor_code.trim()) {
      found.vendor_code = t('form.errors.vendor_code_required');
    }
    if (!values.display_name.trim()) {
      found.display_name = t('form.errors.display_name_required');
    }
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      found.email = t('form.errors.email_invalid');
    }
    if (isContractor && !values.default_account_chart_id) {
      found.default_account_chart_id = t('form.errors.default_account_required');
    }

    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (validate()) {
      onSubmit(values);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop="static" centered>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit ? t('form.title_edit') : t('form.title_create')}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="row">
            <Form.Group className="col-md-6 mb-3">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <Form.Label className="mb-0">
                  {t('form.vendor_code')} <span className="text-danger">*</span>
                </Form.Label>
                <span
                  className="badge rounded-pill"
                  style={{
                    backgroundColor: isQuickBooks ? '#e8f4fd' : '#f1f5f9',
                    color: isQuickBooks ? '#14509e' : '#475569',
                    border: isQuickBooks ? '1px solid #b6d9f7' : '1px solid #cbd5e1',
                    fontWeight: 600,
                    fontSize: '11px',
                    padding: '3px 8px',
                  }}
                >
                  {isQuickBooks
                    ? (values.source === 'qbd' ? t('source_qbd', 'QuickBooks Desktop') : t('source_qbo', 'QuickBooks Online'))
                    : (values.source === 'bank_deposit' ? t('source_bank_deposit', 'Bank Deposit') : t('source_manual', 'Manual Entry'))}
                </span>
              </div>
              <Form.Control
                type="text"
                maxLength={64}
                value={!isEdit && codeLoading ? t('form.vendor_code_generating', 'Generating…') : values.vendor_code}
                disabled={true}
                isInvalid={Boolean(errors.vendor_code)}
              />
              <Form.Control.Feedback type="invalid">{errors.vendor_code}</Form.Control.Feedback>
              <Form.Text muted>
                {isEdit ? t('form.vendor_code_locked') : t('form.vendor_code_auto_help', 'Auto-generated unique vendor code (non-editable)')}
                {values.qbo_id && (
                  <span className="ms-2 text-primary fw-bold">
                    (QuickBooks ID: #{values.qbo_id})
                  </span>
                )}
              </Form.Text>
            </Form.Group>

            <Form.Group className="col-md-6 mb-3">
              <Form.Label>
                {t('form.display_name')} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                maxLength={255}
                value={values.display_name}
                isInvalid={Boolean(errors.display_name)}
                onChange={(event) => setField('display_name', event.target.value)}
              />
              <Form.Control.Feedback type="invalid">{errors.display_name}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="col-md-6 mb-3">
              <Form.Label>{t('form.contact_name')}</Form.Label>
              <Form.Control
                type="text"
                maxLength={255}
                value={values.contact_name}
                onChange={(event) => setField('contact_name', event.target.value)}
              />
            </Form.Group>

            <Form.Group className="col-md-6 mb-3">
              <Form.Label>{t('form.vendor_type')}</Form.Label>
              <Form.Select
                value={values.vendor_type}
                onChange={(event) => handleVendorTypeChange(event.target.value)}
              >
                <option value="">{t('form.not_set')}</option>
                {VENDOR_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{t(option.key)}</option>
                ))}
              </Form.Select>
            </Form.Group>

            {isContractor && (
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>
                  {t('form.default_account')} <span className="text-danger">*</span>
                </Form.Label>
                <AsyncSelect
                  classNamePrefix="react-select"
                  inputId="default_account_chart_id"
                  placeholder={t('form.default_account_placeholder')}
                  noOptionsMessage={() => t('form.default_account_no_options')}
                  loadingMessage={() => t('form.default_account_loading')}
                  isClearable
                  cacheOptions
                  defaultOptions
                  loadOptions={searchAccounts}
                  value={values.default_account_chart_id
                    ? { value: values.default_account_chart_id, label: values.default_account_label }
                    : null}
                  onChange={(selected: any) => {
                    setField('default_account_chart_id', selected ? selected.value : null);
                    setField('default_account_label', selected ? selected.label : '');
                  }}
                />
                {errors.default_account_chart_id && (
                  <Form.Text className="text-danger d-block">{errors.default_account_chart_id}</Form.Text>
                )}
                <Form.Text muted>{t('form.default_account_help')}</Form.Text>
              </Form.Group>
            )}

            <Form.Group className="col-md-6 mb-3">
              <Form.Label>{t('form.email')}</Form.Label>
              <Form.Control
                type="email"
                maxLength={255}
                value={values.email}
                isInvalid={Boolean(errors.email)}
                onChange={(event) => setField('email', event.target.value)}
              />
              <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="col-md-6 mb-3">
              <Form.Label>{t('form.phone')}</Form.Label>
              <Form.Control
                type="text"
                maxLength={64}
                value={values.phone}
                onChange={(event) => setField('phone', event.target.value)}
              />
            </Form.Group>

            <div className="col-12"><hr /></div>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label>{t('form.eligibility')}</Form.Label>
              <Form.Select
                value={values.is_1099_eligible}
                onChange={(event) => setField('is_1099_eligible', event.target.value)}
              >
                {/* Empty is a real third state, not an absence of choice. */}
                <option value="">{t('form.eligibility_derive')}</option>
                <option value="1">{t('form.eligibility_force_include')}</option>
                <option value="0">{t('form.eligibility_force_exclude')}</option>
              </Form.Select>
              <Form.Text muted>{t('form.eligibility_help')}</Form.Text>
            </Form.Group>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label>{t('form.default_form_type')}</Form.Label>
              <Form.Select
                value={values.default_form_type}
                onChange={(event) => handleFormTypeChange(event.target.value)}
              >
                <option value="">{t('form.not_set')}</option>
                {FORM_TYPES.map((formType) => (
                  <option key={formType} value={formType}>{formType}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label>{t('form.default_box')}</Form.Label>
              <Form.Select
                value={values.default_box}
                onChange={(event) => setField('default_box', event.target.value)}
              >
                <option value="">{t('form.not_set')}</option>
                {availableBoxes.map((box) => (
                  <option key={box} value={box}>{box}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label>{t('form.status')}</Form.Label>
              <Form.Select
                value={values.status}
                onChange={(event) => setField('status', event.target.value)}
              >
                <option value="1">{t('status.active')}</option>
                <option value="0">{t('status.inactive')}</option>
                <option value="2">{t('status.archived')}</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="col-md-8 mb-3 d-flex align-items-center">
              <Form.Check
                type="checkbox"
                id="recipient_edelivery_consent"
                label={t('form.edelivery_consent')}
                checked={values.recipient_edelivery_consent}
                onChange={(event) =>
                  setField('recipient_edelivery_consent', event.target.checked)
                }
              />
            </Form.Group>

            <div className="col-12">
              <p className="text-muted small mb-2">{t('form.address_note')}</p>
            </div>

            <Form.Group className="col-md-6 mb-3">
              <Form.Label>{t('form.address_line1')}</Form.Label>
              <Form.Control
                type="text"
                maxLength={255}
                value={values.address_line1}
                onChange={(event) => setField('address_line1', event.target.value)}
              />
            </Form.Group>

            <Form.Group className="col-md-6 mb-3">
              <Form.Label>{t('form.address_line2')}</Form.Label>
              <Form.Control
                type="text"
                maxLength={255}
                value={values.address_line2}
                onChange={(event) => setField('address_line2', event.target.value)}
              />
            </Form.Group>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label>{t('form.city')}</Form.Label>
              <Form.Control
                type="text"
                maxLength={100}
                value={values.city}
                onChange={(event) => setField('city', event.target.value)}
              />
            </Form.Group>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label>{t('form.state')}</Form.Label>
              <Select
                classNamePrefix="react-select"
                placeholder={t('form.select_state', 'Select state')}
                isClearable
                options={STATE_OPTIONS}
                value={
                  STATE_OPTIONS.find((option) => option.value === values.state) ||
                  (values.state ? { value: values.state, label: values.state } : null)
                }
                onChange={(selected) => setField('state', selected ? selected.value : '')}
              />
            </Form.Group>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label>{t('form.postal_code')}</Form.Label>
              <Form.Control
                type="text"
                maxLength={20}
                value={values.postal_code}
                onChange={(event) => setField('postal_code', event.target.value)}
              />
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('form.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={saving || (!isEdit && codeLoading)}>
            {saving ? t('form.saving') : t('form.save')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default VendorFormModal;
